// Journey records. Add a new record here (plus imagery in assets.js) to publish a new journey.
import { ASSETS } from "./assets";

export const JOURNEYS = [
  {
    slug: "tide-trails-goa",
    title: "Tide Trails",
    destination: "Goa",
    audience: "Schools",
    positioning: "A living classroom at the edge of land and sea",
    tagline: "Exploring Balance & Biodiversity",
    image: ASSETS.goa,
    detailImage: ASSETS.goaDetail,
    quickFacts: [
      { label: "Ideal for", value: "Grades 6–12" },
      { label: "Recommended duration", value: "5–6 days" },
      { label: "Suggested months", value: "October–April" },
    ],
    overview: [
      "Where the Arabian Sea meets the Western Ghats, Goa holds an extraordinary concentration of landscapes, ecosystems and cultures. Students explore this environment firsthand, moving from rocky shores and intertidal pools to forest trails, backwaters and local communities.",
      "Along the way, they investigate biodiversity, observe relationships within ecosystems, encounter Goa's cultural heritage and learn to engage with unfamiliar places with curiosity and care.",
    ],
    whyThisPlace: {
      heading: "Where the coast meets the Western Ghats",
      body: [
        "Goa is much more than its beaches. It is a living classroom where the Arabian Sea meets the Western Ghats, one of the world's richest biodiversity hotspots. From tide pools and sandy shores to lush forests and spice plantations, Goa offers students the chance to explore unique ecosystems and learn about biodiversity, sustainability and culture in a safe, engaging environment that sparks curiosity far beyond the classroom.",
        "It is a setting made for hands-on learning, one that connects students with local communities and conservation efforts, bringing textbooks to life in the most meaningful way.",
      ],
    },
    themes: [
      "Ecology & Biodiversity",
      "Heritage & Culture",
      "Community & Connection",
      "Observation & Inquiry",
      "Physical Confidence & Adventure",
      "Sustainability & Stewardship",
    ],
    activities: [
      { title: "Intertidal Discovery", text: "Explore tide pools and uncover the life hidden between land and sea." },
      { title: "Coast & Forest Trails", text: "Walk beaches, forested cliffs and natural landscapes while learning to identify local flora and fauna." },
      { title: "Birding Trails", text: "Observe resident and migratory birds alongside experienced naturalists." },
      { title: "Heritage Walk", text: "Discover Goa's layered history, architecture and cultural influences through its towns and neighbourhoods." },
      { title: "Kayaking", text: "Move through Goa's quieter backwaters and experience its waterways from a different perspective." },
      { title: "Rhythm & Sound", text: "Explore listening, rhythm and collaboration through sound journeys and group percussion." },
    ],
    ancillary: [
      { title: "Surfing", text: "Beginner sessions in gentle waves with certified instructors." },
      { title: "Stand-Up Paddleboarding", text: "Build balance while moving through calm backwaters." },
      { title: "Slacklining", text: "Develop focus and physical confidence through balance." },
    ],
    outcomes: [
      { title: "Ecosystem Literacy", text: "Explore coastal and forest ecosystems firsthand." },
      { title: "Observation & Inquiry", text: "Notice patterns, ask questions and investigate the natural world." },
      { title: "Nature Journaling", text: "Sketch, record and reflect on discoveries in the field." },
      { title: "Physical Confidence", text: "Build balance, resilience and comfort outdoors through movement and exploration." },
      { title: "Community & Place", text: "Understand landscapes through the people, cultures and histories connected to them." },
      { title: "Cultural Heritage", text: "Encounter Goa's layered cultural heritage directly." },
    ],
    inclusions: [
      "Return flights and all transfers",
      "Carefully selected accommodation",
      "All meals",
      "All activities, workshops and entry fees",
      "Dedicated facilitators",
      "Local experts and naturalists",
      "Learning materials",
      "24/7 programme support",
      "One accompanying teacher at no cost for every 15 students",
    ],
    safety: [
      "Every programme is carefully planned and supervised by experienced facilitators, outdoor professionals and local experts.",
      "Trained First Aid responders accompany the group, risk assessments are conducted in advance, and clear participation choices allow students to explore beyond the familiar while feeling supported.",
    ],
    meta: {
      title: "Tide Trails, Goa",
      description:
        "Tide Trails is a place-based school journey in Goa for Grades 6–12: a living classroom at the edge of land and sea, from intertidal ecosystems to the forests of the Western Ghats.",
    },
  },
  {
    slug: "lessons-from-the-wild-pench",
    title: "Lessons from the Wild",
    destination: "Pench",
    audience: "Schools",
    positioning: "A place-based learning journey into the living landscapes of Pench.",
    tagline: "Exploring Ecosystems & Interdependence",
    image: ASSETS.pench,
    quickFacts: [
      { label: "Ideal for", value: "Grades 4–12, with activities adapted to age group" },
      { label: "Recommended duration", value: "4–6 days" },
      { label: "Suggested months", value: "November–May" },
    ],
    overview: [
      "Nestled in the heart of Central India, Pench is a landscape where forests, rivers, wildlife and communities have evolved together over centuries. Every trail, river and rustling canopy tells a real story of connection, inviting students to discover that nature is not simply a collection of species, but a dynamic web of relationships where forests, wildlife and people shape one another in remarkable ways.",
      "Through immersive experiences, including exploring the forest alongside expert naturalists and learning from local artisans and communities, students cultivate curiosity, systems thinking and a lasting appreciation for the delicate balance that sustains life.",
    ],
    whyThisPlace: {
      heading: "A living web of relationships",
      body: [
        "In Pench, students learn to read an ecosystem rather than simply visit one: how forests, rivers, wildlife and people depend on one another, and how communities live alongside the wild. Observation becomes a way of thinking, and systems thinking grows naturally from what students see and record.",
        "We never measure the success of this journey by wildlife sightings. What students take home is a way of seeing: an understanding of how ecosystems hold together, and of their own place within them.",
      ],
    },
    themes: [
      "Ecology & Biodiversity",
      "Community & Connection",
      "Observation & Inquiry",
      "Systems Thinking",
      "Sustainability & Stewardship",
    ],
    activities: [
      { title: "Guided Forest Safari", text: "Explore Pench's forests alongside expert naturalists." },
      { title: "Forest & Birding Trails", text: "Slow observation: tracking signs, birds, insects and plants." },
      { title: "Wildlife Tracking", text: "Read pugmarks, scat, calls and other signs of animal movement." },
      { title: "Camera Trapping", text: "Discover how scientists monitor elusive wildlife." },
      { title: "Forest Ecology Workshops", text: "Hands-on exploration of food webs, biodiversity and ecosystems." },
      { title: "Bushcraft Skills", text: "Learn outdoor survival skills, navigation and teamwork." },
      { title: "Pottery & Traditional Crafts", text: "Experience traditional craftsmanship and create with local artisans." },
      { title: "Community Connections", text: "Meet local communities and learn about life alongside the forest." },
      { title: "Nature Journaling", text: "Sketch, observe and reflect on experiences in the wild." },
    ],
    outcomes: [
      { title: "Ecosystem Literacy", text: "Explore forests, wildlife and their connections." },
      { title: "Wildlife Observation", text: "Notice behaviour, tracks, calls and signs of life." },
      { title: "Community Connection", text: "Engage with communities living alongside the forest." },
      { title: "Conservation Awareness", text: "Explore how wild spaces are protected and cared for." },
      { title: "Outdoor Confidence", text: "Build skills through bushcraft and forest exploration." },
      { title: "Observation & Inquiry", text: "Practise asking questions and noticing patterns." },
    ],
    inclusions: [
      "Return flights and all transfers",
      "3-star or equivalent accommodation",
      "All meals (dietary requirements accommodated)",
      "All activities, workshops, safaris and entry fees",
      "Local experts, naturalists and guides",
      "Educational resources and learning materials",
      "End-to-end logistics support during the programme",
      "One accompanying teacher at no cost for every 15 students",
    ],
    safety: [
      "Journeys are led by experienced facilitators, outdoor professionals, naturalists and local experts, and accompanied by trained First Aid responders.",
      "Careful planning, risk assessments and a dedicated facilitator-to-student ratio ensure appropriate supervision throughout. Forest and safari experiences follow local forest regulations, and students receive responsible wildlife observation and outdoor safety briefings, with activities adapted to weather, terrain and local conditions.",
      "Emotional wellbeing carries equal weight: a culture of inclusion, respect and encouragement in which students feel comfortable to explore, learn, connect and grow.",
    ],
    meta: {
      title: "Lessons from the Wild, Pench",
      description:
        "Lessons from the Wild is a place-based school journey into the living landscapes of Pench for Grades 4–12, centred on ecosystems, relationships, communities, observation and systems thinking.",
    },
  },
];

export const getJourney = (slug) => JOURNEYS.find((j) => j.slug === slug);
